import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import { ILoader, ITool, Viewer } from "@treegram/viewer-core";

import useAuthenticationContext from "@/services/context/authentication/use-authentication-context.ts";

type ViewerWorld = OBC.SimpleWorld<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBF.PostproductionRenderer>;

export interface ViewerContext {
    viewer: Viewer;
    world: ViewerWorld;
    getTool: <T extends ITool>(toolClass: new (...args: never[]) => T) => T | undefined;
    getToolByKeyName: <T extends ITool = ITool>(keyName: string) => T | undefined;
    getLoader: <T extends ILoader>(loaderClass: new (...args: never[]) => T) => T | undefined;
    getLoaders: <T extends ILoader>(loaderClass: new (...args: never[]) => T) => T[];
    getLoaderByKeyName: <T extends ILoader = ILoader>(keyName: string) => T | undefined;
    getLoadersByKeyName: <T extends ILoader = ILoader>(keyName: string) => T[];
}

export interface ViewerTool<T extends ITool = ITool> {
    key: string;
    factory(ctx: ViewerContext): T;
    setup?(tool: T, ctx: ViewerContext): void | Promise<void>;
}

export const createViewerTool = <T extends ITool>(tool: ViewerTool<T>): ViewerTool<T> => tool;

export interface ViewerLoaderOptions<TOptions = unknown> {
    useCoordinates: boolean;
    loaderOptions?: TOptions;
}

export interface ViewerLoader<TOptions = unknown, TLoader extends ILoader<TOptions> = ILoader<TOptions>, TInput = unknown> {
    key: string;
    factory(ctx: ViewerContext): TLoader;
    itemsToLoad: TInput[] | undefined;
    fetchUrl(input: TInput): string | undefined;
    name(input: TInput): string;
    options?: ViewerLoaderOptions<TOptions>;
    getOptions?(input: TInput): ViewerLoaderOptions<TOptions>;
    onItemLoaded?(ctx: ViewerContext, loader: TLoader, input: TInput): void | Promise<void>;
    onError?(ctx: ViewerContext, loader: TLoader, input: TInput, error: Error): void;

    onAllItemsLoaded?(ctx: ViewerContext): void | Promise<void>;
    onUnloaded?(): void | Promise<void>;

    enabled?: boolean;
}

export interface UseViewerOptions<TOptions = unknown> {
    container?: HTMLDivElement | null;
    containerRef?: React.RefObject<HTMLDivElement | null>;
    backgroundColor?: THREE.Color;
    forceResize: boolean;

    viewerLoaders: ViewerLoader<TOptions>[];
    tools?: ViewerTool[];

    /**
     * Appelé une fois que tous les loaders d'un même lot de chargement sont terminés (succès ou échec).
     * N'est pas appelé si le lot a été annulé ou remplacé entre-temps.
     */
    onAllLoadersLoaded?: (ctx: ViewerContext) => void | Promise<void>;
}

export interface ViewerLoadingProgress {
    phase: "initialising" | "loading" | "preparing";
    completed: number;
    total: number;
    failed: number;
}

interface LoaderLoadingState {
    active: boolean;
    completed: number;
    total: number;
    failed: number;
}

export interface UseViewerResult {
    viewer: Viewer | undefined;
    world: ViewerWorld | undefined;
    isLoading: boolean;
    loadingProgress: ViewerLoadingProgress;
    error: Error | undefined;
    retry: () => void;
    getTool: <T extends ITool>(toolClass: new (...args: never[]) => T) => T | undefined;
    getToolByKeyName: <T extends ITool = ITool>(keyName: string) => T | undefined;
    getLoader: <T extends ILoader>(loaderClass: new (...args: never[]) => T) => T | undefined;
    getLoaders: <T extends ILoader>(loaderClass: new (...args: never[]) => T) => T[];
    getLoaderByKeyName: <T extends ILoader = ILoader>(keyName: string) => T | undefined;
    getLoadersByKeyName: <T extends ILoader = ILoader>(keyName: string) => T[];
}

interface ActiveLoaderRecord<TOptions = unknown> {
    loaderDef: ViewerLoader<TOptions, ILoader<TOptions>>;
    instances: ILoader[];
    cancelToken: { isCancelled: boolean };
    loading?: Promise<void>;
    cleanup?: Promise<void>;
    previousCleanup?: Promise<void>;
    pending: Set<Promise<unknown>>;
}

interface ViewerSession extends ViewerContext {
    pending: Set<Promise<unknown>>;
}

const trackOperation = <T,>(pending: Set<Promise<unknown>>, operation: Promise<T>): Promise<T> => {
    pending.add(operation);
    const remove = () => { pending.delete(operation); };
    operation.then(remove, remove);
    return operation;
};

const toError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    return new Error(String(error));
};

const useViewer = (options: UseViewerOptions): UseViewerResult => {
    const {
        container,
        containerRef,
        backgroundColor,
        forceResize,
        viewerLoaders,
        tools,
        onAllLoadersLoaded,
    } = options;

    // Stocké dans une ref pour ne pas relancer l'effet des loaders quand la fonction change.
    const onAllLoadersLoadedRef = useRef(onAllLoadersLoaded);
    onAllLoadersLoadedRef.current = onAllLoadersLoaded;

    const { authenticationResponse } = useAuthenticationContext();
    const token = useMemo(() => authenticationResponse?.baamerToken, [authenticationResponse]);

    const viewerContextRef = useRef<ViewerSession>();
    // A local worker can initialize within one React batch without a visible loading transition.
    // Publish the session itself so tools and loaders always start for the new viewer.
    const [readySession, setReadySession] = useState<ViewerSession>();
    const [viewerError, setError] = useState<Error>();
    const retry = useCallback(() => setError(undefined), []);

    const [isViewerInitialising, setIsViewerInitialising] = useState(false);
    const [loadingLoaders, setLoadingLoaders] = useState<Map<string, LoaderLoadingState>>(() => new Map());
    const isLoading = isViewerInitialising || Array.from(loadingLoaders.values()).some((loader) => loader.active);
    const loadingProgress = useMemo<ViewerLoadingProgress>(() => {
        const counts = Array.from(loadingLoaders.values()).reduce((sum, loader) => ({
            completed: sum.completed + loader.completed,
            total: sum.total + loader.total,
            failed: sum.failed + loader.failed,
        }), { completed: 0, total: 0, failed: 0 });

        if (isViewerInitialising || !readySession) {
            return { phase: "initialising", ...counts };
        }

        if (counts.completed + counts.failed < counts.total) {
            return { phase: "loading", ...counts };
        }

        return { phase: "preparing", ...counts };
    }, [isViewerInitialising, readySession, loadingLoaders]);

    const registeredToolsRef = useRef(new Map<string, ITool>());
    const activeLoadersRef = useRef<Map<string, ActiveLoaderRecord>>(new Map());

    // Memoized (stable references across renders): both only ever read
    // `registeredToolsRef.current` (a ref, always current regardless of
    // when the closure was created), so an empty dependency array is
    // safe. Without this, callers use these as dependencies of effects
    // that themselves trigger a re-render (e.g. a `setState` inside the
    // effect) — a fresh `getTool`/`getToolByKeyName` reference on that
    // re-render would then be seen as "a dependency changed" and
    // re-fire the very same effect a second time, with the exact same
    // logical inputs (e.g. re-running `addMeasure` and duplicating the
    // measure being edited).
    const getTool = useCallback(
        <T extends ITool>(toolClass: new (...args: never[]) => T): T | undefined =>
            Array.from(registeredToolsRef.current.values()).find(
                (tool): tool is T => tool instanceof toolClass,
            ),
        [],
    );

    const getToolByKeyName = useCallback(
        <T extends ITool = ITool>(keyName: string): T | undefined =>
            registeredToolsRef.current.get(keyName) as T | undefined,
        [],
    );

    const getLoader = useCallback(
        <T extends ILoader>(loaderClass: new (...args: never[]) => T): T | undefined =>
            Array.from(activeLoadersRef.current.values())
                .flatMap((record) => record.instances)
                .find((loader): loader is T => loader instanceof loaderClass),
        [],
    );

    const getLoaders = useCallback(
        <T extends ILoader>(loaderClass: new (...args: never[]) => T): T[] =>
            Array.from(activeLoadersRef.current.values())
                .flatMap((record) => record.instances)
                .filter((loader): loader is T => loader instanceof loaderClass),
        [],
    );

    const getLoaderByKeyName = useCallback(
        <T extends ILoader = ILoader>(keyName: string): T | undefined =>
            activeLoadersRef.current.get(keyName)?.instances[0] as T | undefined,
        [],
    );

    const getLoadersByKeyName = useCallback(
        <T extends ILoader = ILoader>(keyName: string): T[] =>
            (activeLoadersRef.current.get(keyName)?.instances ?? []) as T[],
        [],
    );

    const setLoaderLoading = useCallback((key: string, active: boolean, remove = false) => {
        setLoadingLoaders((current) => {
            // Keep completed loaders in the same batch so the denominator stays stable.
            const hasActiveLoaders = Array.from(current.values()).some((loader) => loader.active);
            const next = active && !hasActiveLoaders ? new Map<string, LoaderLoadingState>() : new Map(current);

            if (remove) {
                next.delete(key);
            } else if (active) {
                next.set(key, { active: true, completed: 0, total: 0, failed: 0 });
            } else {
                const previous = next.get(key);
                if (!previous) return current;

                next.set(key, { ...previous, active: false });
            }

            return next;
        });
    }, []);

    const updateLoaderProgress = useCallback((key: string, update: (state: LoaderLoadingState) => LoaderLoadingState) => {
        setLoadingLoaders((current) => {
            const previous = current.get(key);
            if (!previous) return current;

            const next = new Map(current);
            next.set(key, update(previous));
            return next;
        });
    }, []);

    const handleUnload = useCallback((activeLoader: ActiveLoaderRecord) => {
        const { cancelToken, instances, loaderDef } = activeLoader;
        cancelToken.isCancelled = true;
        if (activeLoader.cleanup) return activeLoader.cleanup;
        const cleanup = async () => {
            // Notify removal immediately, before a replacement can publish its own state.
            const notifyUnloaded = async () => {
                try {
                    await loaderDef.onUnloaded?.();
                } catch (error: unknown) {
                    console.error("Erreur pendant le onUnloaded d'un loader", error);
                }
            };
            // load() cannot be aborted: keep its world and fragments alive until it settles.
            await Promise.allSettled([activeLoader.loading, notifyUnloaded()]);
            await Promise.allSettled(instances.map(async (loader) => {
                try {
                    loader.unload();
                    await loader.dispose();
                } catch (error: unknown) {
                    console.error("Erreur pendant le nettoyage d'un loader", error);
                }
            }));
        };
        // eslint-disable-next-line no-param-reassign
        activeLoader.cleanup = trackOperation(activeLoader.pending, cleanup());
        return activeLoader.cleanup;
    }, []);

    // INITIALISATION VIEWER

    useEffect(() => {
        const toolsRef = registeredToolsRef.current;
        const activeLoaders = activeLoadersRef.current;
        
        const containerToUse = container ?? containerRef?.current;
        if (containerToUse == null || viewerError) {
            setIsViewerInitialising(false);
            return undefined;
        }

        setIsViewerInitialising(true);
        let isCancelled = false;
        let resizeObserver: ResizeObserver | undefined;
        let session: ViewerSession | undefined;
        let viewer: Viewer | undefined;
        let graphicsReleased = false;

        const releaseGraphics = () => {
            if (!viewer || graphicsReleased) return;
            graphicsReleased = true;
            // The fragments worker may still be loading, but the abandoned scene
            // must stop rendering and release its GPU context immediately.
            viewer.getComponents().enabled = false;
            const { renderer } = viewer.getWorld() as ViewerWorld;
            if (renderer) {
                renderer.enabled = false;
                renderer.three.dispose();
                renderer.three.forceContextLoss();
                // Keep the canvas attached until Viewer.dispose(): ThatOpen needs
                // its parent to detach renderer listeners before removing it.
            }
        };

        const onContextLost = (event: Event) => {
            // Capture before Three/ThatOpen: restoring only the renderer cannot
            // rebuild fragment buffers whose CPU arrays have already been freed.
            event.stopImmediatePropagation();
            if (isCancelled || graphicsReleased) return;
            releaseGraphics();
            setError(new Error("Le contexte graphique du viewer a été perdu. Relancez le viewer pour recharger la scène."));
        };
        const onContextRestored = (event: Event) => {
            // Also keep retired canvases from restarting ThatOpen's renderer.
            event.stopImmediatePropagation();
        };

        const setupViewer = async () => {
            viewer = new Viewer(
                containerToUse,
                backgroundColor ?? new THREE.Color("#e4ede9"),
            );
            const canvas = viewer.getWorld().renderer?.three.domElement;
            canvas?.addEventListener("webglcontextlost", onContextLost, true);
            canvas?.addEventListener("webglcontextrestored", onContextRestored, true);

            await viewer.init();

            if (isCancelled || graphicsReleased) return;

            session = {
                pending: new Set(),
                viewer,
                world: viewer.getWorld() as ViewerWorld,
                getTool,
                getToolByKeyName,
                getLoader,
                getLoaders,
                getLoaderByKeyName,
                getLoadersByKeyName,
            };
            viewerContextRef.current = session;
            setReadySession(session);

            if (forceResize) {
                resizeObserver = new ResizeObserver(() => {
                    if (!containerToUse || !viewerContextRef.current) return;
                    const { clientWidth, clientHeight } = containerToUse;
                    viewerContextRef.current.world.renderer?.resize(new THREE.Vector2(clientWidth, clientHeight));
                    viewerContextRef.current.world.camera.updateAspect();
                });
                resizeObserver.observe(containerToUse);
            }

            if (!isCancelled) {
                setIsViewerInitialising(false);
            }
        };

        const initialisation = setupViewer().catch((initError: unknown) => {
            console.error("Erreur pendant l'initialisation du viewer", initError);
            releaseGraphics();

            if (!isCancelled) {
                setError(toError(initError));
                setIsViewerInitialising(false);
            }
        });

        return () => {
            isCancelled = true;
            resizeObserver?.disconnect();
            releaseGraphics();

            // Detach synchronously: an old cleanup must never dispose or clear a new viewer.
            const oldSession = session;
            if (viewerContextRef.current === oldSession) viewerContextRef.current = undefined;
            setReadySession(current => {
                if (current === oldSession) {
                    return undefined;
                }

                return current;
            });
            const oldTools = Array.from(toolsRef.values());
            toolsRef.clear();
            Array.from(activeLoaders.values()).forEach((activeLoader) => {
                handleUnload(activeLoader);
            });
            activeLoaders.clear();
            setLoadingLoaders(new Map());

            const cleanup = async () => {
                await initialisation;
                // Includes removed/replaced loaders whose cleanup is still running.
                while (oldSession && oldSession.pending.size > 0) {
                    // eslint-disable-next-line no-await-in-loop
                    await Promise.allSettled(Array.from(oldSession.pending));
                }
                await Promise.allSettled(oldTools.map((tool) => tool.deactivate()));
                // Viewer.dispose owns disposal of registered tools.
                await viewer?.dispose();
            };

            cleanup().catch((error: unknown) => {
                console.error("Erreur pendant le nettoyage du viewer", error);
            });
        };
    }, [
        viewerError,
        backgroundColor,
        container,
        containerRef,
        forceResize,
        handleUnload,
        getTool,
        getToolByKeyName,
        getLoader,
        getLoaders,
        getLoaderByKeyName,
        getLoadersByKeyName,
    ]);

    // GESTION DES OUTILS

    useEffect(() => {
        const ctx = readySession;
        if (ctx == null || isViewerInitialising || tools == null) return undefined;

        let isCancelled = false;

        const setupTools = async () => {
            await Promise.allSettled(
                tools.map(async (tool) => {
                    if (isCancelled) return;

                    const existingTool = registeredToolsRef.current.get(tool.key);
                    if (existingTool != null) return;

                    const instance = tool.factory(ctx);
                    ctx.viewer.registerTool(instance);
                    registeredToolsRef.current.set(tool.key, instance);

                    await tool.setup?.(instance, ctx);
                }),
            );
        };

        trackOperation(ctx.pending, setupTools()).catch((error: unknown) => {
            console.error("Erreur pendant l'initialisation des tools", error);
        });

        return () => {
            isCancelled = true;
        };
    }, [isViewerInitialising, readySession, tools]);

    // LOADERS (3DRTGM / PDF / ETC...) - GESTION DIFFÉRENTIELLE PAR CLE

    useEffect(() => {
        const ctx = readySession;
        if (ctx == null || isViewerInitialising) return undefined;

        const enabledLoaders = viewerLoaders.filter((loader) => loader.enabled ?? true);
        const currentKeys = new Set(enabledLoaders.map((l) => l.key));
        const activeMap = activeLoadersRef.current;

        // 1. Déchargement des clés supprimées
        Array.from(activeMap.entries()).forEach(([key, activeLoader]) => {
            if (!currentKeys.has(key)) {
                handleUnload(activeLoader);
                setLoaderLoading(key, false, true);
                activeMap.delete(key);
            }
        });

        // 2. Identification des loaders modifiés ou nouveaux
        const loadersToLoad: ViewerLoader<unknown, ILoader<unknown>>[] = [];

        enabledLoaders.forEach((viewerLoader) => {
            const existingLoader = activeMap.get(viewerLoader.key);
            const needsReload =
                !existingLoader || existingLoader.loaderDef !== viewerLoader;

            if (needsReload) {
                const previousCleanup = existingLoader ? handleUnload(existingLoader) : undefined;

                const cancelToken = { isCancelled: false };
                const entry: ActiveLoaderRecord = {
                    loaderDef: viewerLoader,
                    instances: [],
                    cancelToken,
                    pending: ctx.pending,
                    previousCleanup,
                };
                activeMap.set(viewerLoader.key, entry);
                loadersToLoad.push(viewerLoader);
            }
        });

        // 3. Chargement asynchrone des loaders nécessitant une mise à jour
        if (loadersToLoad.length > 0) {
            loadersToLoad.forEach((viewerLoader) => {
                setLoaderLoading(viewerLoader.key, true);
            });

            Promise.allSettled(
                loadersToLoad.map((viewerLoader) => {
                    const entry = activeMap.get(viewerLoader.key);
                    if (!entry) return Promise.resolve();
                    const { cancelToken, instances } = entry;

                    const loadItems = async () => {
                        try {
                            // Replacements reuse model IDs: the old loader must finish
                            // unloading before the new one registers its models.
                            if (entry.previousCleanup) await entry.previousCleanup;
                            if (cancelToken.isCancelled) return;
                            const items = viewerLoader.itemsToLoad ?? [];
                            updateLoaderProgress(viewerLoader.key, (state) => ({ ...state, total: items.length }));
                            if (items.length === 0) return;

                            await Promise.allSettled(
                                items.map(async (item) => {
                                    if (cancelToken.isCancelled) return;

                                    let loader: ILoader<unknown> | undefined;

                                    try {
                                        const url = viewerLoader.fetchUrl(item);
                                        if (url == null) {
                                            updateLoaderProgress(viewerLoader.key, (state) => ({
                                                ...state,
                                                total: state.total - 1,
                                            }));
                                            return;
                                        }

                                        loader = viewerLoader.factory(ctx);
                                        instances.push(loader);
                                        const loaderOpts = viewerLoader.getOptions?.(item) ?? viewerLoader.options;

                                        await loader.load(
                                            url,
                                            loaderOpts?.useCoordinates ?? false,
                                            viewerLoader.name(item),
                                            token,
                                            loaderOpts?.loaderOptions,
                                        );

                                        if (cancelToken.isCancelled) {
                                            return;
                                        }

                                        await viewerLoader.onItemLoaded?.(ctx, loader, item);
                                        if (cancelToken.isCancelled) return;

                                        updateLoaderProgress(viewerLoader.key, (state) => ({
                                            ...state,
                                            completed: state.completed + 1,
                                        }));
                                    } catch (error: unknown) {
                                        if (cancelToken.isCancelled) return;
                                        updateLoaderProgress(viewerLoader.key, (state) => ({
                                            ...state,
                                            failed: state.failed + 1,
                                        }));

                                        if (viewerLoader.onError && loader) {
                                            viewerLoader.onError(ctx, loader, item, toError(error));
                                        } else {
                                            console.error(`Erreur pendant le chargement d'un fichier : ${item}`, error);
                                        }
                                    }
                                }),
                            );

                            if (!cancelToken.isCancelled) {
                                await viewerLoader.onAllItemsLoaded?.(ctx);
                            }
                        } finally {
                            if (!cancelToken.isCancelled) {
                                setLoaderLoading(viewerLoader.key, false);
                            }
                        }
                    };
                    entry.loading = trackOperation(ctx.pending, loadItems());
                    return entry.loading;
                }),
            )
                .then(async () => {
                    const isStillCurrent = loadersToLoad.every((viewerLoader) => {
                        const entry = activeMap.get(viewerLoader.key);

                        if (entry == null) return false;
                        if (entry.loaderDef !== viewerLoader) return false;

                        return !entry.cancelToken.isCancelled;
                    });

                    if (!isStillCurrent) return;
                    if (viewerContextRef.current !== ctx) return;

                    await onAllLoadersLoadedRef.current?.(ctx);
                })
                .catch((error: unknown) => {
                    console.error("Erreur pendant le chargement des loaders", error);
                });
        }

        return undefined;
    }, [isViewerInitialising, readySession, token, viewerLoaders, handleUnload, setLoaderLoading, updateLoaderProgress]);

    return {
        viewer: readySession?.viewer,
        world: readySession?.world,
        isLoading,
        loadingProgress,
        error: viewerError,
        retry,
        getTool,
        getToolByKeyName,
        getLoader,
        getLoaders,
        getLoaderByKeyName,
        getLoadersByKeyName,
    };
};

export default useViewer;
